import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bot, ChevronDown, Eraser, LoaderCircle, LockKeyhole, MessageSquareText, PanelRightClose, PanelRightOpen, Send, Settings2, Sparkles } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { fetchRuntimeAgents, invokeAiAgent } from "../../actions/v2/aiSkills";
import { resolveAgentPage, safeQueryContext } from "../../config/agentPageRegistry";
import { useCan } from "../../hooks/useCan";
import constant from "../../constant";
import "./contextual-agent-panel.css";

// Versioned so users who inherited the old desktop-open default start with the
// less intrusive collapsed experience after this product change.
const PANEL_STATE_KEY = "iship_context_agent_open_v2";
const THREAD_KEY = (moduleKey) => `iship_agent_thread:${moduleKey}`;

const readableResult = (value) => {
  if (!value) return "No response was returned.";
  if (typeof value === "string") return value;
  if (typeof value.output_text === "string") return value.output_text;
  if (typeof value.text === "string") return value.text;
  const messages = Array.isArray(value.output)
    ? value.output.flatMap((item) => item?.content || []).map((item) => item?.text).filter(Boolean)
    : [];
  return messages.length ? messages.join("\n\n") : JSON.stringify(value, null, 2);
};

const loadThread = (moduleKey) => {
  try {
    const value = JSON.parse(sessionStorage.getItem(THREAD_KEY(moduleKey)) || "[]");
    return Array.isArray(value) ? value.slice(-30) : [];
  } catch {
    return [];
  }
};

const saveThread = (moduleKey, messages) => {
  try {
    sessionStorage.setItem(THREAD_KEY(moduleKey), JSON.stringify(messages.slice(-30)));
  } catch {
    // Chat still works when browser storage is unavailable.
  }
};

const initialOpen = () => {
  try {
    const saved = localStorage.getItem(PANEL_STATE_KEY);
    if (saved !== null) return saved === "1";
  } catch {
    // Use the responsive default.
  }
  return false;
};

const visiblePageEvidence = () => {
  const page = document.querySelector(".main-content main, .main-content .page-content, .main-content > div");
  if (!page) return "";
  const clone = page.cloneNode(true);
  clone.querySelectorAll("script, style, [data-agent-ignore], .context-agent-panel").forEach((node) => node.remove());
  return String(clone.innerText || clone.textContent || "").replace(/\n{3,}/g, "\n\n").trim().slice(0, 14_000);
};

export default function ContextualAgentPanel() {
  const location = useLocation();
  const adminAccess = useCan("superadmin.dashboard");
  const page = useMemo(() => resolveAgentPage(location.pathname, location.search), [location.pathname, location.search]);
  const [open, setOpen] = useState(initialOpen);
  const [catalogue, setCatalogue] = useState({ agents: [], gateway: {}, module: null });
  const [selectedId, setSelectedId] = useState("");
  const [messages, setMessages] = useState(() => loadThread(page.key));
  const [prompt, setPrompt] = useState("");
  const [externalContext, setExternalContext] = useState({});
  const [includeContext, setIncludeContext] = useState(true);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [launcherHost, setLauncherHost] = useState(undefined);
  const endRef = useRef(null);

  useEffect(() => {
    setLauncherHost(document.getElementById("iship-agent-launcher-slot"));
  }, []);

  useEffect(() => {
    document.body.classList.toggle("iship-agent-panel-open", open);
    try { localStorage.setItem(PANEL_STATE_KEY, open ? "1" : "0"); } catch { /* ignore */ }
    return () => document.body.classList.remove("iship-agent-panel-open");
  }, [open]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setMessages(loadThread(page.key));
    setExternalContext({});
    fetchRuntimeAgents(page.key)
      .then((response) => {
        if (!active) return;
        if (!response?.status) throw new Error(response?.error?.message || "Agent catalogue is unavailable");
        const result = response.result || {};
        const agents = result.agents || [];
        const preferred = agents.find((agent) => agent.slug === page.preferredAgent) || agents[0];
        setCatalogue({ agents, gateway: result.gateway || {}, module: result.module || null });
        setSelectedId(String(preferred?.id || ""));
      })
      .catch((requestError) => {
        if (!active) return;
        setCatalogue({ agents: [], gateway: {}, module: null });
        setSelectedId("");
        setError(requestError?.response?.data?.error?.message || requestError.message || "Agent catalogue is unavailable");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [page.key, page.preferredAgent]);

  useEffect(() => {
    const openAgent = (event) => {
      const detail = event.detail || {};
      if (detail.moduleKey && detail.moduleKey !== page.key) return;
      setOpen(true);
      if (detail.defaultPrompt) setPrompt(String(detail.defaultPrompt));
      if (detail.context && typeof detail.context === "object") setExternalContext(detail.context);
    };
    window.addEventListener("iship:open-agent", openAgent);
    return () => window.removeEventListener("iship:open-agent", openAgent);
  }, [page.key]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, running, open]);

  const selectedAgent = useMemo(
    () => catalogue.agents.find((agent) => String(agent.id) === String(selectedId)) || null,
    [catalogue.agents, selectedId]
  );
  const usable = Boolean(catalogue.gateway?.enabled && selectedAgent);

  const updateMessages = (next) => {
    setMessages(next);
    saveThread(page.key, next);
  };

  const clearThread = () => {
    updateMessages([]);
    setPrompt("");
    setError("");
  };

  const send = async () => {
    const request = prompt.trim();
    if (!request || !selectedAgent || running) return;
    const history = messages.slice(-20).map(({ role, content }) => ({ role, content }));
    const userMessage = { role: "user", content: request, at: new Date().toISOString() };
    updateMessages([...messages, userMessage]);
    setPrompt("");
    setRunning(true);
    setError("");
    try {
      const response = await invokeAiAgent(selectedAgent.id, {
        module_key: page.key,
        page_title: document.title || page.name,
        page_path: `${location.pathname}${location.search}`,
        prompt: request,
        messages: history,
        context: includeContext ? {
          route: page.name,
          query: safeQueryContext(location.search),
          visible_page_text: visiblePageEvidence(),
          supplied_page_context: externalContext,
        } : {},
      });
      if (!response?.status) throw new Error(response?.error?.message || "Agent request failed");
      const content = readableResult(response.result?.result || response.result);
      updateMessages([...messages, userMessage, { role: "assistant", content, at: new Date().toISOString(), agent: selectedAgent.name }]);
    } catch (requestError) {
      const message = requestError?.response?.data?.error?.message || requestError.message || "Agent request failed";
      setError(message);
      updateMessages([...messages, userMessage, { role: "error", content: message, at: new Date().toISOString() }]);
    } finally {
      setRunning(false);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  const launcher = (
    <button
      type="button"
      className="context-agent__rail"
      onClick={() => setOpen(true)}
      aria-label={`Open ${page.name} agent`}
      title={`Open ${selectedAgent?.name || `${page.name} Agent`}`}
    >
      <span><Sparkles size={17} /></span>
      <strong>{selectedAgent?.name || `${page.name} Agent`}</strong>
      <PanelRightOpen size={17} />
    </button>
  );

  return (
    <>
      {!open && launcherHost ? createPortal(launcher, launcherHost) : null}
      <aside className={`context-agent${open ? " is-open" : ""}`} data-agent-ignore aria-label={`${page.name} agent`}>
        {!open && launcherHost === null ? launcher : null}
        {open ? (
        <section className="context-agent__panel">
          <header className="context-agent__header">
            <span className="context-agent__mark"><Bot size={20} /></span>
            <div><small>ISHIP INTELLIGENCE</small><h2>{page.name}</h2></div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Collapse agent panel"><PanelRightClose size={19} /></button>
          </header>

          <div className="context-agent__identity">
            {loading ? <span className="context-agent__loading"><LoaderCircle size={15} /> Loading page agent…</span> : null}
            {!loading && catalogue.agents.length ? (
              <label>
                <span>Page specialist</span>
                <div><select value={selectedId} onChange={(event) => setSelectedId(event.target.value)}>{catalogue.agents.map((agent) => <option key={agent.id} value={agent.id}>{agent.name}</option>)}</select><ChevronDown size={15} /></div>
              </label>
            ) : null}
            {selectedAgent ? <p>{selectedAgent.description}<small><LockKeyhole size={11} /> {String(selectedAgent.approval_mode || "review_required").replaceAll("_", " ")} · revision {selectedAgent.revision}</small></p> : null}
          </div>

          <div className="context-agent__messages" aria-live="polite">
            {!messages.length && !running ? (
              <div className="context-agent__empty"><MessageSquareText size={26} /><strong>Ask about this page</strong><p>{page.prompt}</p><button type="button" onClick={() => setPrompt(page.prompt)}>Use suggested request</button></div>
            ) : null}
            {messages.map((message, index) => (
              <article key={`${message.at || index}-${index}`} className={`context-agent__message context-agent__message--${message.role}`}>
                <span>{message.role === "assistant" ? message.agent || selectedAgent?.name || "Agent" : message.role === "error" ? "Request error" : "You"}</span>
                <pre>{message.content}</pre>
              </article>
            ))}
            {running ? <div className="context-agent__thinking"><LoaderCircle size={16} /><span>{selectedAgent?.name || "Agent"} is reviewing governed page context…</span></div> : null}
            <div ref={endRef} />
          </div>

          {!loading && !catalogue.gateway?.enabled ? <div className="context-agent__notice"><strong>Gateway not connected</strong><span>A Superadmin must connect and enable OpenClaw in Agent Studio.</span>{adminAccess.canView ? <Link to={constant.Authedcomponent.aiSkillsSettings.url}><Settings2 size={14} /> Open Agent Studio</Link> : null}</div> : null}
          {!loading && catalogue.gateway?.enabled && !catalogue.agents.length ? <div className="context-agent__notice"><strong>No active agent for this page</strong><span>A Superadmin can enable or assign an agent to {page.name}.</span>{adminAccess.canView ? <Link to={constant.Authedcomponent.aiSkillsSettings.url}><Settings2 size={14} /> Manage agents</Link> : null}</div> : null}
          {error ? <div className="context-agent__error">{error}</div> : null}

          <footer className="context-agent__composer">
            <label className="context-agent__evidence"><input type="checkbox" checked={includeContext} onChange={(event) => setIncludeContext(event.target.checked)} /><span>Include visible page evidence</span></label>
            <textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} onKeyDown={onKeyDown} rows="3" placeholder={`Ask ${selectedAgent?.name || "the page agent"}…`} disabled={!usable || running} />
            <div><button type="button" className="context-agent__clear" onClick={clearThread} disabled={!messages.length && !prompt}><Eraser size={15} /> Clear</button><span>Review only · Enter to send</span><button type="button" className="context-agent__send" onClick={send} disabled={!usable || running || !prompt.trim()}><Send size={16} /><span>Send</span></button></div>
          </footer>
        </section>
        ) : null}
      </aside>
    </>
  );
}
